/**
 * customerAutofillService.js — P0 SPRINT (Aug 2026)
 *
 * CANONICAL CUSTOMER AUTOFILL ENGINE (frontend).
 *
 * Doel:
 *   ONE CUSTOMER · ONE ADDRESS · ONE SHIPMENT · ONE NAVIGATION FLOW
 *
 * Alle modules (Shipment, Warehouse, Telefoonregistratie, AddPiece,
 * Pickup, Delivery, Offertes, Facturen) gebruiken UITSLUITEND deze
 * service — geen module normaliseert klant-velden zelf.
 *
 * De legacy veld-mapping (address/street/address_line_1,
 * postal_code/zipcode/zip, house_number/number, mobile/mobile_phone,
 * vat/btw, kvk/chamber_of_commerce, whatsapp/whatsapp_number, ...)
 * gebeurt centraal in ``normalizeCustomer()``.
 *
 * ``customerToSenderParty(c)`` en ``customerToRecipientParty(c)`` mappen
 * de canonical payload naar de sender/recipient party-shape gebruikt in
 * ShipmentForm/WarehousePackageForm/EditShipmentForm.
 */
import { adminApi } from "../context/AuthContext";

// ─── Legacy alias mapping ────────────────────────────────────────────
// EERSTE non-empty waarde wint (source-priority).
const ALIASES = {
  street:                ["street", "street_name", "address_line_1", "address_line1", "address"],
  house_number:          ["house_number", "housenumber", "number", "house_no", "streetnumber", "street_number"],
  house_number_addition: ["house_number_addition", "housenumber_addition", "addition", "suffix", "house_number_suffix"],
  postal_code:           ["postal_code", "postcode", "zipcode", "zip_code", "zip"],
  city:                  ["city", "place", "town", "locality"],
  province:              ["province", "state", "region", "county"],
  country:               ["country", "country_name", "country_code_name"],
  phone:                 ["phone", "telephone", "phone_number"],
  mobile:                ["mobile", "mobile_phone", "mobile_number", "cell", "cell_phone"],
  whatsapp:              ["whatsapp", "whatsapp_number", "whats_app"],
  email:                 ["email", "email_address", "e_mail"],
  contact_person:        ["contact_person", "contactperson", "contact_name", "attention_of", "atn"],
  first_name:            ["first_name", "firstname", "given_name"],
  last_name:             ["last_name", "lastname", "surname", "family_name"],
  company_name:          ["company_name", "company", "organization", "organisation", "business_name"],
  display_name:          ["display_name", "name", "full_name"],
  customer_number:       ["customer_number", "klantnummer", "kn", "external_customer_number"],
  vat_number:            ["vat_number", "btw_number", "vat", "btw"],
  kvk_number:            ["kvk_number", "kvk", "chamber_of_commerce", "coc_number", "coc"],
  eori_number:           ["eori_number", "eori"],
  tax_number:            ["tax_number", "belastingnummer", "fiscal_number", "tin", "tax_id"],
  preferred_language:    ["preferred_language", "language", "locale", "lang"],
  customer_type:         ["customer_type", "type"],
  notes:                 ["notes", "notitie", "internal_notes", "remarks"],
};

function pick(src, keys) {
  if (!src || typeof src !== "object") return null;
  for (const k of keys) {
    const v = src[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return null;
}

function canonicalAddressFrom(src) {
  return {
    street:                pick(src, ALIASES.street),
    house_number:          pick(src, ALIASES.house_number),
    house_number_addition: pick(src, ALIASES.house_number_addition),
    postal_code:           pick(src, ALIASES.postal_code),
    city:                  pick(src, ALIASES.city),
    province:              pick(src, ALIASES.province),
    country:               pick(src, ALIASES.country),
  };
}

function mergeAddress(...sources) {
  const merged = {
    street: null, house_number: null, house_number_addition: null,
    postal_code: null, city: null, province: null, country: null,
  };
  for (const src of sources) {
    if (!src) continue;
    const c = canonicalAddressFrom(src);
    for (const [k, v] of Object.entries(c)) {
      if ((merged[k] === null || merged[k] === "") && v) merged[k] = v;
    }
  }
  return merged;
}

// ─── Normalisatie ────────────────────────────────────────────────────

/**
 * Normaliseer een klant-object naar canonical veldnamen. Idempotent.
 * Aanvaardt zowel raw backend-documents als de canonical autofill-payload.
 */
export function normalizeCustomer(doc) {
  if (!doc || typeof doc !== "object") return null;

  const primary = doc.primary_address || {};
  const shipping = doc.shipping_address || {};
  const billing = doc.billing_address || {};
  const address = mergeAddress(primary, shipping, billing, doc);

  const altRaw = Array.isArray(doc.alt_addresses) ? doc.alt_addresses : [];
  const altAddresses = altRaw.map((a) => ({
    id: a.id || null,
    type: (a.type || "other").toLowerCase(),
    label: a.label || null,
    ...canonicalAddressFrom(a),
    is_default: !!a.is_default,
  }));

  const displayName = pick(doc, ALIASES.display_name)
    || [pick(doc, ALIASES.first_name), pick(doc, ALIASES.last_name)].filter(Boolean).join(" ").trim()
    || pick(doc, ALIASES.company_name)
    || pick(doc, ALIASES.email);

  return {
    id: doc.id || doc.customer_id || null,
    customer_id: doc.id || doc.customer_id || null,
    customer_number: pick(doc, ALIASES.customer_number),
    display_name: displayName || null,
    first_name: pick(doc, ALIASES.first_name),
    last_name: pick(doc, ALIASES.last_name),
    company_name: pick(doc, ALIASES.company_name),
    contact_person: pick(doc, ALIASES.contact_person),
    customer_type: pick(doc, ALIASES.customer_type),
    phone: pick(doc, ALIASES.phone),
    mobile: pick(doc, ALIASES.mobile),
    whatsapp: pick(doc, ALIASES.whatsapp),
    email: pick(doc, ALIASES.email),
    ...address,
    primary_address: address,
    alt_addresses: altAddresses,
    vat_number: pick(doc, ALIASES.vat_number),
    kvk_number: pick(doc, ALIASES.kvk_number),
    eori_number: pick(doc, ALIASES.eori_number),
    tax_number: pick(doc, ALIASES.tax_number),
    preferred_language: pick(doc, ALIASES.preferred_language),
    notes: pick(doc, ALIASES.notes),
    // Batch 3 · Fase 3/5 — Additional contacts blijven canonical zichtbaar
    // op de klant-payload zodat de UI ze in een ContactPersonSelector kan
    // tonen. Elk element bevat {id, name, email, phone, phone_norm, role}.
    additional_contacts: Array.isArray(doc.additional_contacts) ? doc.additional_contacts : [],
    is_agent: !!doc.is_agent,
  };
}

// ─── Batch 3 · Fase 5 — Contact-selectie helpers ─────────────────────

/**
 * Bouw de lijst van contactpersonen die de UI in een selector toont.
 * Eerste optie is altijd de PRIMAIRE contactgegevens van de klant.
 * Aanvullende contacten worden erna weergegeven met hun rol/functie.
 *
 * Retourneert `[{key, label, sublabel, isPrimary, contact}]` waar
 *  - key             — unieke selector-key (primary | contact-{id})
 *  - contact         — {name, email, phone, role} — wat je in de party wilt patchen
 */
export function buildContactOptions(customerRaw) {
  const c = normalizeCustomer(customerRaw);
  if (!c) return [];
  const options = [];
  const primaryName = c.display_name
    || [c.first_name, c.last_name].filter(Boolean).join(" ").trim()
    || c.company_name
    || "";
  options.push({
    key: "primary",
    label: primaryName || "Primair contact",
    sublabel: c.email || c.phone || c.mobile || "",
    isPrimary: true,
    contact: {
      name: primaryName,
      email: c.email || "",
      phone: c.phone || "",
      mobile: c.mobile || "",
      whatsapp: c.whatsapp || "",
      role: null,
    },
  });
  for (const ac of c.additional_contacts || []) {
    if (!ac || !ac.name) continue;
    options.push({
      key: `contact-${ac.id || ac.name}`,
      label: ac.name,
      sublabel: [ac.role, ac.email || ac.phone].filter(Boolean).join(" · "),
      isPrimary: false,
      contact: {
        name: ac.name,
        email: ac.email || "",
        phone: ac.phone || "",
        // Aanvullende contacten hebben geen apart whatsapp/mobile veld
        // (scope-freeze user: alleen naam/email/telefoon/functie).
        // Value uit klant blijft leidend voor whatsapp/mobile.
        mobile: c.mobile || "",
        whatsapp: c.whatsapp || "",
        role: ac.role || null,
      },
    });
  }
  return options;
}

/**
 * Patch een party-object met contact-specifieke communicatievelden.
 * customer_id/adres blijven ONGEWIJZIGD (customer_id is leidend).
 * Wordt gebruikt door ContactPersonSelector.
 */
export function applyContactToParty(party, contact) {
  if (!party || !contact) return party;
  return {
    ...party,
    name: contact.name || party.name || "",
    email: contact.email !== undefined ? (contact.email || "") : party.email || "",
    phone: contact.phone !== undefined ? (contact.phone || "") : party.phone || "",
    mobile: contact.mobile !== undefined ? (contact.mobile || "") : party.mobile || "",
    whatsapp: contact.whatsapp !== undefined ? (contact.whatsapp || "") : party.whatsapp || "",
    // Contact-role wordt als contact_person meegegeven zodat downstream
    // documenten (offerte/factuur/paklijst) de juiste persoon tonen.
    contact_person: contact.name || party.contact_person || "",
  };
}

// ─── Party mapping (shipment sender/recipient) ───────────────────────

/**
 * Convert customer → shipment party-shape.
 *
 * Gebruikt in EditShipmentForm en WarehousePackageForm om sender/recipient
 * blokken te vullen na klant-selectie. Lege velden blijven leeg.
 */
export function customerToParty(customerRaw) {
  const c = normalizeCustomer(customerRaw);
  if (!c) return null;
  const name = c.display_name
    || [c.first_name, c.last_name].filter(Boolean).join(" ").trim()
    || c.company_name
    || "";
  return {
    name,
    company: c.company_name || "",
    contact_person: c.contact_person || "",
    phone: c.phone || "",
    mobile: c.mobile || "",
    whatsapp: c.whatsapp || "",
    email: c.email || "",
    street: c.street || "",
    house_number: c.house_number || "",
    house_number_addition: c.house_number_addition || "",
    postal_code: c.postal_code || "",
    city: c.city || "",
    province: c.province || "",
    country: c.country || "",
    // Bewaar id/klantnummer voor "Ook klantgegevens bijwerken" flow.
    customer_id: c.customer_id,
    customer_number: c.customer_number || null,
  };
}

// Aliases voor semantische duidelijkheid in de UI.
export const customerToSenderParty = customerToParty;
export const customerToRecipientParty = customerToParty;

// ─── API-calls ────────────────────────────────────────────────────────

/**
 * Fetch canonical autofill-payload voor een klant.
 * Handig als er meer velden nodig zijn dan de entity-search metadata biedt
 * (bijv. voor alt_addresses of preferred_language).
 */
export async function fetchCustomerAutofill(customerId) {
  if (!customerId) return null;
  const r = await adminApi.get(`/admin/customers/${customerId}/autofill`);
  return normalizeCustomer(r.data);
}

// ─── Override detection ──────────────────────────────────────────────

/**
 * Detecteer of de gebruiker een adres/contact-veld heeft gewijzigd t.o.v.
 * de originele klant-payload. Retourneert een array van { field, old, new }
 * items voor gebruik in de "Ook klantgegevens bijwerken" dialog.
 *
 * Velden zoals `contact_person` en `whatsapp` op de shipment-party zijn
 * primair klant-attributen — als gebruiker die daar aanpast willen we vragen
 * om ook de klantkaart bij te werken.
 */
export const OVERRIDE_FIELDS = [
  "phone", "mobile", "whatsapp", "email",
  "street", "house_number", "house_number_addition",
  "postal_code", "city", "province", "country",
];

export function detectAddressOverrides(originalCustomer, currentParty) {
  if (!originalCustomer || !currentParty) return [];
  const c = normalizeCustomer(originalCustomer);
  const diffs = [];
  for (const f of OVERRIDE_FIELDS) {
    const oldV = (c[f] ?? "").toString().trim();
    const newV = (currentParty[f] ?? "").toString().trim();
    if (oldV !== newV && newV !== "") {
      diffs.push({ field: f, old: c[f] || null, new: currentParty[f] });
    }
  }
  return diffs;
}
